/**
 * KeyboardShortcutsPanel — modal cheatsheet for the global shortcuts
 * registered in AppShell + the editor. Triggered by '?'.
 *
 * The panel is purely presentational; the actual key-bindings are
 * registered in AppShell via useShortcuts. We keep this list in sync
 * by hand — there are few enough shortcuts that this is fine.
 */
import { useEffect } from 'react';

interface ShortcutRow {
  keys: string[];
  label: string;
}

interface ShortcutSection {
  title: string;
  rows: ShortcutRow[];
}

const SECTIONS: ShortcutSection[] = [
  {
    title: 'Navigation',
    rows: [
      { keys: ['⌘', 'K'], label: 'Open command palette' },
      { keys: ['g', 'd'], label: 'Go to Dashboard' },
      { keys: ['g', 'f'], label: 'Go to Flows' },
      { keys: ['g', 'e'], label: 'Go to Event Designs' },
      { keys: ['g', 'm'], label: 'Go to Mesh' },
      { keys: ['g', 'r'], label: 'Go to Runs / Monitor' },
      { keys: ['g', 'l'], label: 'Go to Logs' },
      { keys: ['g', 't'], label: 'Go to Live Trace' },
    ],
  },
  {
    title: 'Editing (in flow / event editors)',
    rows: [
      { keys: ['⌘', 'S'], label: 'Save' },
      { keys: ['⌘', 'Z'], label: 'Undo' },
      { keys: ['⌘', '⇧', 'Z'], label: 'Redo' },
      { keys: ['⌘', 'D'], label: 'Duplicate selection' },
      { keys: ['⌘', 'A'], label: 'Select all' },
      { keys: ['Del'], label: 'Delete selection' },
    ],
  },
  {
    title: 'Actions',
    rows: [
      { keys: ['⌘', '↵'], label: 'Deploy current flow' },
      { keys: ['?'], label: 'Open this shortcut sheet' },
      { keys: ['Esc'], label: 'Close modal / palette' },
    ],
  },
  {
    title: 'Lists & rows',
    rows: [
      { keys: ['↑', '↓'], label: 'Navigate rows' },
      { keys: ['Space'], label: 'Toggle row selection' },
      { keys: ['D'], label: 'Deploy focused row' },
      { keys: ['U'], label: 'Undeploy focused row' },
      { keys: ['Del'], label: 'Delete focused row' },
    ],
  },
];

export interface KeyboardShortcutsPanelProps {
  open: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsPanel({ open, onClose }: KeyboardShortcutsPanelProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="cmdk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
      data-testid="shortcuts-panel"
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 92vw)',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: 0,
        }}
      >
        <div
          className="card__header"
          style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}
        >
          <div className="card__title">Keyboard shortcuts</div>
          <span style={{ flex: 1 }} />
          <a
            href="/docs/power-user.md"
            target="_blank"
            rel="noreferrer"
            className="btn btn--ghost"
            style={{ height: 24, padding: '0 8px', fontSize: 12 }}
            data-testid="shortcuts-full-reference"
          >
            Full reference ↗
          </a>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            style={{ height: 24, padding: '0 8px', fontSize: 12 }}
            data-testid="shortcuts-close"
          >
            Close
          </button>
        </div>
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-3)',
                  marginBottom: 8,
                }}
              >
                {s.title}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {s.rows.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'var(--text-2)',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
                    <span style={{ display: 'inline-flex', gap: 3 }}>
                      {r.keys.map((k, ki) => (
                        <kbd
                          key={ki}
                          style={{
                            display: 'inline-block',
                            minWidth: 18,
                            padding: '1px 6px',
                            fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--text-1)',
                            background: 'var(--bg-surface-2)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            textAlign: 'center',
                          }}
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
